/**
 * Переключение оформления.
 *
 * Тема применяется двумя способами одновременно, и это не дублирование:
 *
 *  1. `localStorage['kairo.theme']` читает inline-скрипт в `index.html` ещё до
 *     первой отрисовки — иначе при запуске тёмного приложения мелькал бы белый
 *     экран;
 *  2. `document.documentElement.dataset.theme` — то, на что смотрит CSS
 *     (`@custom-variant dark` в `theme.css`).
 *
 * Выбор пользователя дополнительно хранится в профиле: это настройка аккаунта,
 * которая доедет до других устройств при синхронизации. Локальное хранилище —
 * лишь кеш для мгновенного старта.
 */

import { create } from 'zustand';

import type { ThemeMode } from '@/shared/db/schema';

const STORAGE_KEY = 'kairo.theme';

export type ResolvedTheme = 'light' | 'dark';

export const THEME_OPTIONS: { value: ThemeMode; label: string }[] = [
  { value: 'light', label: 'Светлая' },
  { value: 'dark', label: 'Тёмная' },
  { value: 'system', label: 'Как в системе' },
];

const darkQuery =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function systemTheme(): ResolvedTheme {
  return darkQuery?.matches ? 'dark' : 'light';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode;
}

function readStoredMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    /* приватный режим: остаётся системная тема */
  }
  return 'system';
}

function applyTheme(mode: ThemeMode): ResolvedTheme {
  const resolved = resolveTheme(mode);

  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolved;
  }
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* без хранилища выбор проживёт до перезагрузки */
  }

  return resolved;
}

interface ThemeState {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  /** Выбор пользователя в настройках. */
  setMode: (mode: ThemeMode) => void;
  /** Тема пришла из профиля (другое устройство, первый вход) — принимаем её. */
  syncFromProfile: (mode: ThemeMode) => void;
}

const initialMode = readStoredMode();

export const useThemeStore = create<ThemeState>((set) => ({
  mode: initialMode,
  resolved: applyTheme(initialMode),

  setMode: (mode) => set({ mode, resolved: applyTheme(mode) }),

  syncFromProfile: (mode) =>
    set((state) => (state.mode === mode ? state : { mode, resolved: applyTheme(mode) })),
}));

/**
 * Системная тема может смениться на ходу (расписание «тёмная ночью»).
 * Подписка живёт всё время работы приложения — отписываться некому и незачем.
 */
darkQuery?.addEventListener('change', () => {
  const { mode } = useThemeStore.getState();
  if (mode === 'system') useThemeStore.setState({ resolved: applyTheme('system') });
});
